import React from 'react';
import { Grade } from '../../services/api/domains/grades/types';
import { modalStyles as styles } from './styles';
import useEditableModal from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import { gradeFields, gradeAlwaysDisabledFields } from './configs/gradeFields';
import LgmButton from '../LgmButton';
import { Edit, Save, X } from 'lucide-react';

interface GradeModalProps {
  grade: Grade;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedGrade: Grade) => void;
  close: () => void;
}

const GradeModal: React.FC<GradeModalProps> = ({
  grade,
  initialMode = 'VIEW',
  save,
  close,
}) => {
  // Use the hook for all modal logic
  const {
    editedEntity: editedGrade,
    setEditedEntity: setEditedGrade,
    modalMode,
    hasUnsavedChanges,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getPrimaryActionText,
    isSaving,
  } = useEditableModal({
    entity: grade,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: gradeAlwaysDisabledFields,
    styles,
  });

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.modalContentStyle}>
        <div style={styles.modalHeaderStyle}>
          <h2 style={{ ...styles.modalTitleStyle, marginBottom: 0 }}>
            {modalMode === 'VIEW'
              ? 'Product Type Details'
              : modalMode === 'EDIT'
                ? 'Edit Product Type'
                : 'Add Product Type'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LgmButton
              variant="flat"
              color={modalMode === 'VIEW' ? 'warning' : 'save'}
              onClick={handleSave}
              disabled={
                isSaving || (modalMode === 'EDIT' && !hasUnsavedChanges)
              }
              style={styles.modalHeaderIconTextButtonStyle}
              aria-label={getPrimaryActionText()}
              title={getPrimaryActionText()}>
              {modalMode === 'VIEW' ? <Edit size={16} /> : <Save size={16} />}
              {getPrimaryActionText()}
            </LgmButton>
            <LgmButton
              variant="flat"
              onClick={handleClose}
              disabled={isSaving}
              style={styles.modalCloseXStyle}
              hoverStyle={styles.modalCloseXHoverStyle}
              pressedStyle={styles.modalCloseXPressedStyle}
              aria-label="Close">
              <X size={16} />
            </LgmButton>
          </div>
        </div>

        <div style={styles.modalBodyStyleNoFooter}>
          {/* Grade fields */}
          <ModalFieldRenderer
            fields={gradeFields}
            entity={editedGrade}
            setEntity={setEditedGrade}
            mode={modalMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            isFieldDisabled={isFieldDisabled}
            getFloatingLabelStyle={getFloatingLabelStyle}
            getFloatingInputStyle={getFloatingInputStyle}
            styles={styles}
            missingMessage="Unknown"
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(GradeModal);
